// src/lib/orderReturn.ts
import { emit } from './events';
import { sendLocalNotification } from './notify';
import { getAuth } from 'firebase/auth';
import {
  addCoins as apiAddCoins,
  markOutbound as apiMarkOutbound,
  markCompletion as apiMarkCompletion,
} from './api';
import { setTokens } from './tokens'; // <- existing setter from tokens.ts
import { track } from './track';
import { addSavings } from '../store/savings';
import { logError } from './logger';

export type OutboundCtx = {
  id?: string;               // backend id from /api/orders/outbound
  ts: number;
  showAfterTs?: number;
  returnedAt?: number;
  promptedAt?: number;
  restaurantId: string;
  restaurantName: string;
  restaurantUrl?: string;
  dishName?: string;
  platform: 'swiggy' | 'zomato' | string;
  otherPlatform?: 'swiggy' | 'zomato' | string;
  compareRowId?: string;
  comparePairId?: string;
  total: number;
  otherTotal?: number;
  delta?: number;            // otherTotal - total (positive means we saved)
  savingsKind?: 'verified' | 'estimated' | 'unknown';
  tokenReward: number;       // Bits to award on confirm
  deepLink: string;
  targetUrl?: string;
  items?: Array<{ name: string; qty: number; price?: number }>;
  breakdown?: {
    subtotal?: number;
    discount?: number;
    fees?: number;
    tax?: number;
    delivery?: number;
    total?: number;
  };
};

const KEY = 'bw.outbound.order';
const RETURN_BANNER_DELAY_MS = 12_000;
const RETURN_BANNER_REPROMPT_MS = 90_000;

function readStored(): OutboundCtx | null {
  const parse = (raw: string | null) => {
    if (!raw) return null;
    try { return JSON.parse(raw) as OutboundCtx; } catch { return null; }
  };
  const ls = parse(localStorage?.getItem?.(KEY) || null);
  if (ls) return ls;
  return parse(sessionStorage?.getItem?.(KEY) || null);
}

function writeStored(ctx: OutboundCtx) {
  try { localStorage.setItem(KEY, JSON.stringify(ctx)); } catch {}
  try { sessionStorage.setItem(KEY, JSON.stringify(ctx)); } catch {}
}

function updateStored(mutator: (ctx: OutboundCtx) => OutboundCtx | null) {
  const current = readStored();
  if (!current) return null;
  const next = mutator(current);
  if (!next) return null;
  writeStored(next);
  return next;
}

export function resolveSavingsKind(ctx: Pick<OutboundCtx, 'savingsKind' | 'total' | 'otherTotal' | 'delta'>): 'verified' | 'estimated' | 'unknown' {
  if (ctx.savingsKind === 'verified' || ctx.savingsKind === 'estimated' || ctx.savingsKind === 'unknown') {
    return ctx.savingsKind;
  }
  const hasTotal = Number.isFinite(ctx.total);
  const hasOtherTotal = Number.isFinite(ctx.otherTotal);
  const hasDelta = Number.isFinite(ctx.delta);
  if (hasTotal && hasOtherTotal && hasDelta) return 'verified';
  if ((hasTotal && hasOtherTotal) || hasDelta || hasTotal || hasOtherTotal) return 'estimated';
  return 'unknown';
}

export function getPendingReturn(): OutboundCtx | null {
  try {
    const ctx = readStored();
    if (!ctx) return null;
    // expire after 2h
    if (Date.now() - ctx.ts > 2 * 60 * 60 * 1000) {
      clearPendingReturn();
      return null;
    }
    const normalized: OutboundCtx = {
      ...ctx,
      showAfterTs: Number.isFinite(ctx.showAfterTs) ? ctx.showAfterTs : ctx.ts + RETURN_BANNER_DELAY_MS,
      savingsKind: resolveSavingsKind(ctx),
    };
    return normalized;
  } catch {
    return null;
  }
}

export function getReturnBannerCandidate(now = Date.now()): OutboundCtx | null {
  const ctx = getPendingReturn();
  if (!ctx) return null;
  if (!Number.isFinite(ctx.returnedAt)) return null;
  if ((ctx.showAfterTs || 0) > now) return null;
  if (Number.isFinite(ctx.promptedAt) && now - Number(ctx.promptedAt) < RETURN_BANNER_REPROMPT_MS) return null;
  return ctx;
}

export function markReturnBannerShown(now = Date.now()) {
  updateStored((ctx) => ({ ...ctx, promptedAt: now }));
}

export function clearPendingReturn() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
  try {
    localStorage.removeItem(KEY);
  } catch {}
}

/** 🔔 Fire a hint when user returns to the tab and we still have a pending order. */
export function initReturnListener() {
  let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    const ctx = getPendingReturn();
    if (!ctx) return;
    const didLeave = Number.isFinite(hiddenAt) && Number(hiddenAt) >= ctx.ts;
    if (didLeave) {
      updateStored((curr) => ({
        ...curr,
        returnedAt: Date.now(),
        showAfterTs: Number.isFinite(curr.showAfterTs) ? curr.showAfterTs : curr.ts + RETURN_BANNER_DELAY_MS,
      }));
    }
    if (getReturnBannerCandidate()) {
      emit('bw:return:possible', null);
    }
  };
  const onFocus = () => {
    const ctx = getPendingReturn();
    if (!ctx) return;
    const didLeave = Number.isFinite(hiddenAt) && Number(hiddenAt) >= ctx.ts;
    if (didLeave) {
      updateStored((curr) => ({
        ...curr,
        returnedAt: Date.now(),
        showAfterTs: Number.isFinite(curr.showAfterTs) ? curr.showAfterTs : curr.ts + RETURN_BANNER_DELAY_MS,
      }));
    }
    if (getReturnBannerCandidate()) {
      emit('bw:return:possible', null);
    }
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    onVisible();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  window.addEventListener('pageshow', onFocus);
  window.addEventListener('resume', onFocus as any);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('pageshow', onFocus);
    window.removeEventListener('resume', onFocus as any);
  };
}

/**
 * Start an outbound order:
 * 1. stash locally so ReturnBanner can show even if offline
 * 2. ping backend /api/orders/outbound so we get a server-side event id
 */
export async function startOutbound(ctx: OutboundCtx): Promise<void> {
  // (1) keep local
  writeStored({
    ...ctx,
    showAfterTs: Number.isFinite(ctx.showAfterTs) ? ctx.showAfterTs : ctx.ts + RETURN_BANNER_DELAY_MS,
    returnedAt: undefined,
    promptedAt: undefined,
    savingsKind: resolveSavingsKind(ctx),
  });

  // (2) ask backend to create outbound record
  try {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) {
      // user not signed in => we can't attribute to Firestore yet.
      return;
    }

    const total = Number.isFinite(ctx.total) ? ctx.total : undefined;
    const otherTotal = Number.isFinite(ctx.otherTotal) ? ctx.otherTotal : undefined;
    const delta = Number.isFinite(ctx.delta) ? ctx.delta : undefined;
    const items = Array.isArray(ctx.items)
      ? ctx.items
          .filter((it) => String(it?.name || '').trim())
          .map((it) => ({
            name: String(it.name).trim(),
            qty: Number.isFinite(it.qty) && it.qty > 0 ? Math.round(it.qty) : 1,
            price: Number.isFinite(it.price) ? it.price : undefined,
          }))
      : undefined;
    const breakdown = ctx.breakdown
      ? {
          subtotal: Number.isFinite(ctx.breakdown.subtotal) ? ctx.breakdown.subtotal : undefined,
          discount: Number.isFinite(ctx.breakdown.discount) ? ctx.breakdown.discount : undefined,
          fees: Number.isFinite(ctx.breakdown.fees) ? ctx.breakdown.fees : undefined,
          tax: Number.isFinite(ctx.breakdown.tax) ? ctx.breakdown.tax : undefined,
          delivery: Number.isFinite(ctx.breakdown.delivery) ? ctx.breakdown.delivery : undefined,
          total: Number.isFinite(ctx.breakdown.total) ? ctx.breakdown.total : undefined,
        }
      : undefined;

    // NEW SHAPE:
    // markOutbound(payload) no longer needs user_id; backend injects from token.
    // We'll send pricing + context. We DO NOT care about coins here.
    const outboundRes = await apiMarkOutbound({
      platform: ctx.platform,
      partner: ctx.platform, // for now keep partner/platform compatibility
      other_platform: ctx.otherPlatform,
      restaurant: ctx.restaurantName,
      restaurant_id: ctx.restaurantId,
      restaurant_url: ctx.restaurantUrl,
      dish_name: ctx.dishName || ctx.restaurantName,
      compare_row_id: ctx.compareRowId,
      compare_pair_id: ctx.comparePairId,
      target_url: ctx.targetUrl || ctx.deepLink,
      savings_kind: ctx.savingsKind,
      total,
      otherTotal,
      delta,
      items,
      breakdown,
    });

    if (outboundRes?.id) {
      const withId: OutboundCtx = { ...ctx, id: String(outboundRes.id) };
      writeStored(withId);
      track('compare_outbound', {
        platform: ctx.platform,
        restaurant: ctx.restaurantName,
        delta: Number.isFinite(ctx.delta) ? ctx.delta : undefined,
      });
    }
  } catch (err) {
    logError('startOutbound failed', { err: String(err) }, { toast: true });
  }
}

/**
 * User taps "I placed it" in ReturnBanner.
 *
 * We do 4 things:
 *  1. update local token balance immediately (optimistic)
 *  2. call /api/users/coins/add with (amount, reason)
 *  3. call /api/orders/complete so backend:
 *      - finalizes the order event
 *      - bumps totals / leaderboard / achievements
 *      - rate-limits repeat completions
 *  4. toast + local notification
 *
 * Returns summary for debugging.
 */
export async function confirmOrderPlaced(): Promise<{
  saved: number;
  tokensAwarded: number;
  ctx: OutboundCtx | null;
}> {
  const ctx = getPendingReturn();

  if (!ctx) {
    return { saved: 0, tokensAwarded: 0, ctx: null };
  }

  // compute savings we *think* they got
  let saved = 0;
  if (ctx) {
    const raw = ctx.delta ?? 0;
    saved = Math.max(0, Math.round(raw));
  }

  // --- 1. optimistic local tokens ---
  let tokens = 0;
  try {
    tokens = Number(localStorage.getItem('bw.tokens') || '0');
  } catch {}
  const award = ctx?.tokenReward ?? 0;
  const newBal = tokens + award;

  setTokens(newBal);
  try {
    localStorage.setItem('bw.tokens', String(newBal));
  } catch {}

  emit('bw:tokens:gain', { amount: award, balance: newBal });
  emit('bw:reward', { amount: award, balance: newBal });

  // --- 2 & 3. server sync (fire-and-forget style, but awaited where it matters) ---
  try {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;

    // 2) award coins server-side
    //    NEW SIGNATURE: addCoins(amount, reason)
    if (uid && award > 0) {
      // we *don't* await this strictly; coins failing shouldn't block completion
      apiAddCoins(award, 'order:return').catch(() => {});
    }

    // 3) ensure we have an outbound id; if missing, recreate it now
    let outboundId = ctx?.id;
    if (!outboundId && ctx) {
      try {
        const res = await apiMarkOutbound({
          platform: ctx.platform,
          partner: ctx.platform,
          other_platform: ctx.otherPlatform,
          restaurant: ctx.restaurantName,
          restaurant_id: ctx.restaurantId,
          restaurant_url: ctx.restaurantUrl,
          dish_name: ctx.dishName || ctx.restaurantName,
          compare_row_id: ctx.compareRowId,
          compare_pair_id: ctx.comparePairId,
          target_url: ctx.targetUrl || ctx.deepLink,
          savings_kind: ctx.savingsKind,
          total: Number.isFinite(ctx.total) ? ctx.total : undefined,
          otherTotal: Number.isFinite(ctx.otherTotal) ? ctx.otherTotal : undefined,
          delta: Number.isFinite(ctx.delta) ? ctx.delta : undefined,
          items: Array.isArray(ctx.items) ? ctx.items : undefined,
          breakdown: ctx.breakdown,
        });
        if (res?.id) {
          outboundId = String(res.id);
          writeStored({ ...ctx, id: outboundId });
        }
      } catch (err) {
        logError('apiMarkOutbound (retry) failed', { err: String(err) });
      }
    }

    if (outboundId) {
      await apiMarkCompletion(outboundId, saved);
      emit('bw:orders:refresh', null);
      track('order_complete', {
        platform: ctx.platform,
        restaurant: ctx.restaurantName,
        saved,
        award,
      });
    }
  } catch (err) {
    logError('confirmOrderPlaced sync failed', { err: String(err) }, { toast: true });
  }

  // --- 4. UX feedback ---
  if (saved > 0) {
    try {
      addSavings(saved);
    } catch {
      /* ignore local persistence issues */
    }
  }
  if (saved > 0) {
    emit('bw:toast', {
      title: 'Nice savings!',
      body: `You saved ~₹${saved}`,
    });
    sendLocalNotification(
      'Order saved!',
      `You saved about ₹${saved}. +${award} Bits`
    );
  } else {
    emit('bw:toast', {
      title: 'Order recorded',
      body: `+${award} Bits added`,
    });
    sendLocalNotification(
      'Order recorded',
      `+${award} Bits added to your balance`
    );
  }

  // clear banner state
  clearPendingReturn();

  return { saved, tokensAwarded: award, ctx };
}
